/**
 * Profile editor (Phase 44.4).
 *
 * Step 1 of the tenant-admin setup flow. Edits the drafted
 * TenantProfile: basic identity, ICP segments, competitors, value
 * propositions, vocabulary, sales methodology.
 *
 * Autosave is owned by the setupStore — every change dispatches
 * `updateProfile(patch)` which optimistically merges locally, queues a
 * debounced PUT, and surfaces success/failure through the floating
 * `AutosaveIndicator` on TenantSetupPage.
 *
 * Regeneration lives in `ResearchPanel` at top-right — firing it stops
 * further edits through the polling state until completion (Phase 44.7
 * ships the full modal; this PR surfaces the status inline on the
 * panel).
 */

import { Link } from "react-router-dom";
import { useSetupStore } from "../../stores/setupStore.js";
import RepeatableTextList from "./components/RepeatableTextList.jsx";
import VocabularyListEditor from "./components/VocabularyListEditor.jsx";
import TagInput from "./components/TagInput.jsx";
import WordCountedTextarea from "./components/WordCountedTextarea.jsx";
import ResearchPanel from "./components/ResearchPanel.jsx";

function ValidationErrors({ errors }) {
  if (!errors?.errors?.length) return null;
  return (
    <div className="validation-errors" role="alert">
      <strong>Validation errors — latest save was rejected:</strong>
      <ul>
        {errors.errors.map((e, i) => (
          <li key={i}>{e}</li>
        ))}
      </ul>
    </div>
  );
}

export default function ProfileEditor() {
  const profile = useSetupStore((s) => s.profile);
  const updateProfile = useSetupStore((s) => s.updateProfile);
  const validationErrors = useSetupStore((s) => s.validationErrors.profile);

  const set = (key, value) => updateProfile({ [key]: value });

  if (!profile) {
    return (
      <div className="setup-section">
        <h2>Tell us about your company</h2>
        <p className="setup-section__empty">
          No profile drafted yet. The bootstrap pipeline hasn&apos;t completed
          or produced a profile. Use the Regenerate button in the research
          panel, or wait for the in-flight bootstrap to finish.
        </p>
        <ResearchPanel section="profile" metadata={null} />
      </div>
    );
  }

  return (
    <div className="setup-section setup-section--split">
      <div className="setup-section__main">
        <div className="setup-section__header">
          <h2>Tell us about your company</h2>
          <p className="setup-section__muted">
            Step 1 of 3. Edits save automatically; the indicator at the
            bottom-right confirms each write.
          </p>
        </div>

        <ValidationErrors errors={validationErrors} />

        {/* ── Basic ─────────────────────────────────────────────────────── */}
        <fieldset className="setup-fieldset">
          <legend>Basic</legend>

          <label className="setup-field">
            <span className="setup-field__label">Company name</span>
            <input
              type="text"
              value={profile.name || ""}
              readOnly
              aria-readonly="true"
              className="setup-field__input setup-field__input--readonly"
            />
            <span className="setup-field__help">
              Set during provisioning; contact a super-admin to change.
            </span>
          </label>

          <label className="setup-field">
            <span className="setup-field__label">Domain</span>
            <input
              type="text"
              value={profile.domain || ""}
              readOnly
              aria-readonly="true"
              className="setup-field__input setup-field__input--readonly"
            />
          </label>

          <label className="setup-field">
            <span className="setup-field__label">Industry</span>
            <input
              type="text"
              value={profile.industry || ""}
              onChange={(e) => set("industry", e.target.value)}
              maxLength={100}
              placeholder="e.g. Cloud Security, Enterprise SaaS"
              className="setup-field__input"
            />
            <span className="setup-field__help">
              A short label, not a taxonomy. One line.
            </span>
          </label>

          <WordCountedTextarea
            label="Description"
            value={profile.description || ""}
            onChange={(v) => set("description", v)}
            softLimit={100}
            helpText="What does your company do? Keep it under 100 words — longer descriptions inflate every agent prompt."
          />
        </fieldset>

        {/* ── ICP Segments ──────────────────────────────────────────────── */}
        <fieldset className="setup-fieldset">
          <legend>ICP segments</legend>
          <RepeatableTextList
            items={profile.icp_segments || []}
            onChange={(v) => set("icp_segments", v)}
            placeholder="e.g. Enterprise FinServ"
            addLabel="Add segment"
            helpText="The customer segments you actively target."
            maxLength={200}
          />
        </fieldset>

        {/* ── Competitors ───────────────────────────────────────────────── */}
        <fieldset className="setup-fieldset">
          <legend>Competitors</legend>
          <TagInput
            tags={profile.competitors || []}
            onChange={(v) => set("competitors", v)}
            placeholder="Type competitor, press Enter"
            helpText="Named competitors only — agents won't invent competitors outside this list."
            maxLength={200}
          />
        </fieldset>

        {/* ── Value Props ───────────────────────────────────────────────── */}
        <fieldset className="setup-fieldset">
          <legend>Value propositions</legend>
          <RepeatableTextList
            items={profile.value_props || []}
            onChange={(v) => set("value_props", v)}
            placeholder="e.g. Reduce cloud misconfig blast radius in minutes"
            addLabel="Add value prop"
            helpText="Concise statements. Agents use these to frame opportunities."
            maxLength={500}
          />
        </fieldset>

        {/* ── Vocabulary ────────────────────────────────────────────────── */}
        <fieldset className="setup-fieldset">
          <VocabularyListEditor
            items={profile.vocabulary || []}
            onChange={(v) => set("vocabulary", v)}
          />
        </fieldset>

        {/* ── Sales methodology ─────────────────────────────────────────── */}
        <fieldset className="setup-fieldset">
          <legend>Sales methodology</legend>
          <label className="setup-field">
            <span className="setup-field__label">Methodology</span>
            <select
              className="setup-field__input"
              value={profile.sales_methodology?.type || "seven_stage"}
              onChange={(e) =>
                set("sales_methodology", {
                  ...(profile.sales_methodology || {}),
                  type: e.target.value,
                })
              }
            >
              <option value="seven_stage">Seven-stage (Wiz default)</option>
            </select>
            <span className="setup-field__help">
              Only the Wiz seven-stage methodology is supported in v4.2.1.
            </span>
          </label>
        </fieldset>

        <div className="setup-footer">
          <Link to="/setup/catalogue" className="btn btn-primary">
            Save &amp; continue
          </Link>
        </div>
      </div>

      <div className="setup-section__side">
        <ResearchPanel
          section="profile"
          metadata={profile.research_metadata}
        />
      </div>
    </div>
  );
}
